import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getManagerPanel } from '../../services/migration/personnelDataService';
import { resolveCashPermissions } from './useManagerCashPermissions';
import { resolveProfitLossPermission } from './useManagerProfitLossPermission';
import { resolveCourierPermissions } from '../couriers/module/courierPermissions';

/**
 * Yönetici Raporlar sayfasında gösterilebilen raporlar. Yalnızca şube filtresi olan ve
 * backend'i yöneticiyi kendi şubesiyle sınırlayan raporlar burada yer alır.
 */
export type ManagerReportId =
  | 'endOfDay'
  | 'endOfDayList'
  | 'restaurantOrders'
  | 'allOrders'
  | 'profitLoss'
  | 'accountMovements'
  | 'cashMovements'
  | 'courierReport'
  | 'taskReports';

export interface ManagerReportPermissions {
  allowed: ManagerReportId[];
  loaded: boolean;
}

const NONE: ManagerReportPermissions = { allowed: [], loaded: false };

/** Her rapor, yönetici panelinde onu açan sekmenin yetki kuralıyla aynı kurala bağlıdır. */
export const resolveManagerReports = (settings: any): ManagerReportId[] => {
  const s = settings || {};
  const salesSubs = s['salesReports_subPermissions'] || {};
  const rules: Record<ManagerReportId, boolean> = {
    endOfDay: s['salesReports'] === true,
    endOfDayList: s['salesReports'] === true && salesSubs['salesReports_endOfDay'] === true,
    restaurantOrders: s['salesReports'] === true,
    allOrders: s['salesReports'] === true,
    profitLoss: resolveProfitLossPermission(s),
    accountMovements: s['current'] === true,
    cashMovements: resolveCashPermissions(s).canViewMovements,
    courierReport: resolveCourierPermissions(s).canReport,
    taskReports: s['reports'] === true,
  };
  return (Object.keys(rules) as ManagerReportId[]).filter(id => rules[id]);
};

/** Şube yöneticisinin görebileceği raporlar. `enabled` false ise istek atılmaz. */
export const useManagerReportPermissions = (enabled: boolean): ManagerReportPermissions => {
  const { currentUser } = useAuth();
  const [perms, setPerms] = useState<ManagerReportPermissions>(NONE);

  useEffect(() => {
    if (!enabled || !currentUser?.uid) {
      setPerms(NONE);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mp = await getManagerPanel(currentUser.uid);
        if (cancelled) return;
        setPerms({ allowed: resolveManagerReports(mp.found ? mp.settings : null), loaded: true });
      } catch (e) {
        console.warn('[useManagerReportPermissions] managerPanel okunamadı:', e);
        if (!cancelled) setPerms({ ...NONE, loaded: true });
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, currentUser?.uid]);

  return perms;
};
